// Cross-device Firebase merge helpers.
// Quests used to be one array field. Replacing that array from a stale iPhone
// cache wiped other-device items, and waiting for a server snapshot meant
// Safari often never wrote new quests at all. Per-item `todoMap.{id}` writes
// can land as soon as the doc is known to exist.

export const TODO_MAP_FIELD = 'todoMap';

// Firestore refuses ~ * / [ ] anywhere in a field path and an empty path segment.
// updateDoc() throws that synchronously, so one bad key does not just lose its own
// write — it fails the whole patch, which is then re-queued and retried forever,
// blocking every later save. Repair paths on the way out instead.
// Keep these separate: a /g/ regex carries lastIndex between .test() calls.
const FORBIDDEN_FIELD_PATH_CHARS = /[~*/[\]]/g;
const HAS_FORBIDDEN_FIELD_PATH_CHAR = /[~*/[\]]/;

export const sanitizeFieldPathSegment = (segment) =>
  String(segment ?? '').replace(FORBIDDEN_FIELD_PATH_CHARS, '_');

export const isSafeFieldPath = (key) => {
  const segments = String(key ?? '').split('.');
  return segments.every((seg) => seg !== '' && !HAS_FORBIDDEN_FIELD_PATH_CHAR.test(seg));
};

export const sanitizeCloudFieldPaths = (updates) => {
  const out = {};
  Object.entries(updates || {}).forEach(([key, value]) => {
    const safeKey = String(key)
      .split('.')
      .map(sanitizeFieldPathSegment)
      .filter((seg) => seg !== '')
      .join('.');
    if (!safeKey) return;
    out[safeKey] = value;
  });
  return out;
};

export const isFirestoreQuotaError = (err) => {
  const code = String(err?.code || '');
  const message = String(err?.message || '');
  return code.includes('resource-exhausted')
    // The REST path reports the same condition as an HTTP status, not an SDK code.
    || /\b429\b/.test(message)
    || /quota exceeded/i.test(message)
    || /resource-exhausted/i.test(message);
};

// Firestore never rejects a write when the daily quota is gone. RESOURCE_EXHAUSTED
// is retryable on the write stream, so the SDK parks the mutation and backs off
// forever — updateDoc() neither resolves nor rejects. Silence is therefore the only
// signal there is, and an app that reads silence as success keeps queueing writes
// against an empty meter. A write still unsettled after this long is treated as a
// spent quota. Comfortably longer than a slow mobile write, far shorter than the
// SDK's maximum backoff.
export const WRITE_STALL_DEADLINE_MS = 20 * 1000;

// Firestore's daily free-tier budget is spent for the day once the backend answers
// resource-exhausted. Retrying just burns reads against an empty meter and pushes
// the SDK's backoff longer, so hold off entirely and probe occasionally instead.
// The probe matters: without it the app stays dead until a manual reload.
export const QUOTA_PROBE_INTERVAL_MS = 10 * 60 * 1000;

export const quotaBlockedUntil = (now = Date.now()) => now + QUOTA_PROBE_INTERVAL_MS;

export const isQuotaBlocked = (blockedUntil, now = Date.now()) =>
  Number.isFinite(Number(blockedUntil)) && Number(blockedUntil) > now;

export const QUOTA_SAVE_MESSAGE = 'Firebase daily quota reached — sync resumes after reset';

// A queued-write notice must never paper over the quota message: that is what left
// "Syncing meals to Firebase…" spinning for minutes instead of naming the real cause.
export const cloudSaveStateForWrite = ({ quotaBlocked, pending } = {}) => (
  quotaBlocked
    ? { status: 'error', reason: 'quota', message: QUOTA_SAVE_MESSAGE }
    : { status: 'saving', message: pending ? 'Waiting for Firebase…' : 'Saving to Firebase…' }
);

// A sync loop always looks the same from here: the identical payload written over
// and over. v2.0.7's key migration did exactly that and spent a full day of
// Firestore writes in an hour. Suppressing a byte-identical repeat kills any such
// loop at the source, whatever caused it, without limiting genuine edits.
const CLOUD_SIGNATURE_SKIP_KEYS = new Set(['updatedAt']);

export const isFirestoreSentinel = (value) =>
  !!value && typeof value === 'object' && typeof value._methodName === 'string';

export const cloudWriteSignature = (payload) => {
  const entries = Object.entries(payload || {})
    .filter(([key]) => !CLOUD_SIGNATURE_SKIP_KEYS.has(key))
    .sort(([a], [b]) => a.localeCompare(b));
  if (!entries.length) return '';
  return JSON.stringify(entries);
};

// Never suppress a patch carrying a delete: deleteField() sentinels all serialize
// to {}, so two different deletions would look like the same write.
export const shouldSkipDuplicateWrite = ({ signature, lastSignature, payload } = {}) => {
  if (!signature || signature !== lastSignature) return false;
  return !Object.values(payload || {}).some(isFirestoreSentinel);
};

// Hard ceiling on writes, well under Firestore's 20k/day Spark allowance. The
// duplicate-write guard stops a loop that repeats one payload; this stops a loop
// whose payload keeps changing (an embedded timestamp is enough to defeat a
// signature). Real use is a few hundred writes a day, so these limits are far
// above normal and far below the cap. Blocked writes are parked and coalesced,
// never dropped — the flush merges a batch into a single write.
export const WRITE_BUDGET_PER_HOUR = 200;
export const WRITE_BUDGET_PER_DAY = 2000;
const HOUR_MS = 60 * 60 * 1000;

export const emptyWriteLedger = () => ({ day: '', dayCount: 0, hourStart: 0, hourCount: 0 });

export const rollWriteLedger = (ledger, now = Date.now()) => {
  const today = new Date(now).toISOString().slice(0, 10);
  const base = { ...emptyWriteLedger(), ...(ledger || {}) };
  const next = base.day === today
    ? base
    : { day: today, dayCount: 0, hourStart: now, hourCount: 0 };
  if (!next.hourStart || now - next.hourStart >= HOUR_MS) {
    return { ...next, hourStart: now, hourCount: 0 };
  }
  return next;
};

export const cloudWriteBudget = (ledger, now = Date.now()) => {
  const rolled = rollWriteLedger(ledger, now);
  const overHour = rolled.hourCount >= WRITE_BUDGET_PER_HOUR;
  const overDay = rolled.dayCount >= WRITE_BUDGET_PER_DAY;
  return { ledger: rolled, allowed: !overHour && !overDay, overHour, overDay };
};

export const countCloudWrite = (ledger, now = Date.now()) => {
  const rolled = rollWriteLedger(ledger, now);
  return { ...rolled, dayCount: rolled.dayCount + 1, hourCount: rolled.hourCount + 1 };
};

export const BUDGET_SAVE_MESSAGE =
  'Sync paused — unusual save activity. Your edits are safe on this device.';

export const INCREMENTAL_CLOUD_KEYS = new Set([
  'mealLog', 'calorieMemory', 'stepLog', 'moneyLog', 'healthProfile',
  'dayPlans', 'habits', 'scheduleConfig', 'scheduleData', 'deepseekApiKey',
  'tabOrder', 'monthlyJournal', 'updatedAt', 'sprintBoard', 'integrations', 'goals',
  'todos', 'todoMap', 'stats', 'penaltyMeta', 'header', 'dominos', 'dominoLabels',
  'completedDominos', 'categoryLabels', 'collapsedCategories', 'collapsedTodos',
  'userApiKey', 'userApiKey2', 'visionBoardText'
]);

export const cloudUpdateRootKey = (key) => String(key || '').split('.')[0];

const patchKeys = (updates) => Object.keys(updates || {}).filter(
  (k) => k !== 'updatedAt' && k !== '_calorieMemoryRemove' && k !== '_dateKeyRemove'
);

export const isIncrementalCloudUpdate = (updates) => {
  const keys = patchKeys(updates);
  return keys.length > 0 && keys.every((k) => INCREMENTAL_CLOUD_KEYS.has(cloudUpdateRootKey(k)));
};

export const isFullDocumentCloudUpdate = (updates) => {
  const keys = new Set(patchKeys(updates).map(cloudUpdateRootKey));
  return keys.has('todos') && keys.has('habits') && keys.has('mealLog') && keys.has('dayPlans');
};

export const canWriteCloudMerge = ({
  hydrated,
  writeReady,
  updates,
  fullDocument = false
} = {}) => {
  const fullDoc = fullDocument || isFullDocumentCloudUpdate(updates);
  if (fullDoc) return !!hydrated;
  if (hydrated) return true;
  if (!writeReady) return false;
  const keys = patchKeys(updates);
  const roots = keys.map(cloudUpdateRootKey);
  // Replacing the whole todos array from a cache-only view would wipe items
  // saved on other devices. Per-item todoMap writes are safe.
  if (roots.includes('todos')) return false;
  return keys.length > 0 && keys.every((k) => INCREMENTAL_CLOUD_KEYS.has(cloudUpdateRootKey(k)));
};

const asTodoList = (value) => (Array.isArray(value) ? value : []);

const asMap = (value) => (value && typeof value === 'object' && !Array.isArray(value) ? value : {});

export const todoMapKey = (id) => String(id ?? '');

export const isValidTodoMapKey = (id) => /^[A-Za-z0-9_-]+$/.test(todoMapKey(id));

// Quest order used to live only in the `todos` array, but incremental saves write
// per-item `todoMap` entries and a Firestore map has no ordering. Every snapshot
// therefore rebuilt a fresh sequence and rows jumped. `order` makes the sequence a
// fixed point: it is stored on the record, so all devices and all snapshot paths
// derive the same list.
export const TODO_ORDER_STEP = 1000;

export const todoOrderValue = (todo) => {
  const n = Number(todo?.order);
  return Number.isFinite(n) ? n : null;
};

// Quests written before `order` existed have none, and every quest on this account is
// in that state: incremental todoMap patches were the only writes. Their fallback must
// therefore be something every device already agrees on. Ids are `Date.now()` at
// creation, so they give a stable creation order on any snapshot path.
const todoIdSortKey = (todo) => {
  const str = todoMapKey(todo?.id);
  const num = Number(str);
  return { str, num: Number.isFinite(num) ? num : null };
};

// Stable ascending sort. Quests carrying an order come first in that order; the rest
// follow in id order.
export const sortTodosByOrder = (todos) => {
  const list = asTodoList(todos);
  return list
    .map((todo, idx) => ({ todo, idx }))
    .sort((a, b) => {
      const ao = todoOrderValue(a.todo);
      const bo = todoOrderValue(b.todo);
      if (ao !== null && bo === null) return -1;
      if (ao === null && bo !== null) return 1;
      if (ao !== null && bo !== null && ao !== bo) return ao - bo;
      // Never fall back to the incoming array index. That index is exactly the unstable
      // thing `order` exists to fix — the cached and server snapshot paths concatenate
      // their sources differently, so using it leaves un-migrated quests oscillating
      // between two orderings on every snapshot.
      const ak = todoIdSortKey(a.todo);
      const bk = todoIdSortKey(b.todo);
      if (ak.num !== null && bk.num !== null && ak.num !== bk.num) return ak.num - bk.num;
      if (ak.str === bk.str) return 0;
      return ak.str < bk.str ? -1 : 1;
    })
    .map((entry) => entry.todo);
};

// Re-stamp a list into a clean 0, 1000, 2000 … sequence. Called on every save so a
// drag persists the new positions, and the gaps leave room without renumbering everything.
export const resequenceTodoOrder = (todos) =>
  asTodoList(todos).map((todo, idx) => ({ ...todo, order: idx * TODO_ORDER_STEP }));

const todoStampMs = (todo) => {
  if (typeof todo?.updatedAt === 'string' && todo.updatedAt) {
    const ms = new Date(todo.updatedAt).getTime();
    if (Number.isFinite(ms)) return ms;
  }
  const idNum = Number(todo?.id);
  return Number.isFinite(idNum) ? idNum : 0;
};

const liveTodo = (todo) => {
  if (!todo || typeof todo !== 'object' || todo.deleted) return null;
  const { deleted: _deleted, ...rest } = todo;
  return rest;
};

export const todosToMap = (todos) => {
  const map = {};
  asTodoList(todos).forEach((todo) => {
    const id = todoMapKey(todo?.id);
    if (!id || !isValidTodoMapKey(id)) return;
    map[id] = { ...todo, id: todo.id };
  });
  return map;
};

export const todoMapToList = (map) => {
  const list = [];
  Object.entries(asMap(map)).forEach(([k, todo]) => {
    const live = liveTodo(todo);
    if (live) list.push({ ...live, id: live.id ?? k });
  });
  return list;
};

const pickNewerTodo = (a, b) => {
  if (!a) return b || null;
  if (!b) return a;
  return todoStampMs(a) >= todoStampMs(b) ? a : b;
};

// Union local + cloud quests by id. Tombstones / deletedIds drop an item.
// Do NOT use the document updatedAt as a cutoff: a later meal save would
// make a just-added iPhone quest look "older than cloud" and get discarded.
export const mergeTodoState = (localTodos, cloudTodos, {
  deletedIds = [],
  localMap = {},
  cloudMap = {}
} = {}) => {
  const deleted = new Set((deletedIds || []).map((id) => todoMapKey(id)).filter(Boolean));
  const safeMap = (m) => {
    const out = {};
    Object.entries(asMap(m)).forEach(([k, v]) => {
      if (v && typeof v === 'object') {
        out[k] = { ...v, id: v.id ?? k };
      }
    });
    return out;
  };
  const map = { ...todosToMap(cloudTodos), ...safeMap(cloudMap) };
  const incoming = { ...todosToMap(localTodos), ...safeMap(localMap) };
  Object.entries(incoming).forEach(([id, todo]) => {
    const winner = pickNewerTodo(map[id], todo);
    // The copy that wins on updatedAt may be the one that predates `order` — cloud
    // todoMap entries were all written before order existed, and equal stamps resolve
    // in their favour. Carry a position across rather than discard it, or a device that
    // has already migrated would lose its order on the next snapshot.
    const other = winner === map[id] ? todo : map[id];
    const carried = todoOrderValue(other);
    map[id] = (winner && todoOrderValue(winner) === null && carried !== null)
      ? { ...winner, order: carried }
      : winner;
  });
  deleted.forEach((id) => {
    const cur = map[id];
    map[id] = {
      id: cur?.id ?? id,
      deleted: true,
      updatedAt: new Date().toISOString()
    };
  });

  const list = [];
  const seen = new Set();
  const pushLive = (todo) => {
    const id = todoMapKey(todo?.id);
    if (!id || seen.has(id)) return;
    const rec = map[id];
    const live = liveTodo(rec);
    if (!live) return;
    seen.add(id);
    list.push(live);
  };
  asTodoList(cloudTodos).forEach(pushLive);
  todoMapToList(cloudMap).forEach(pushLive);
  asTodoList(localTodos).forEach(pushLive);
  Object.values(map).forEach(pushLive);
  return { list: sortTodosByOrder(list), map };
};

export const mergeInFlightTodos = (localTodos, cloudTodos, _cloudUpdatedAt, opts = {}) =>
  mergeTodoState(localTodos, cloudTodos, opts).list;

export const buildTodoMapPatch = (prevTodos, nextTodos, stamp = new Date().toISOString()) => {
  const prev = todosToMap(prevTodos);
  const next = todosToMap(nextTodos);
  const patch = {};
  Object.entries(next).forEach(([id, todo]) => {
    if (!isValidTodoMapKey(id)) return;
    const prevJson = JSON.stringify(prev[id] || null);
    const nextRec = { ...todo, updatedAt: todo.updatedAt || stamp };
    if (prevJson !== JSON.stringify(nextRec) && prevJson !== JSON.stringify(todo)) {
      patch[`${TODO_MAP_FIELD}.${id}`] = nextRec;
    }
  });
  Object.keys(prev).forEach((id) => {
    if (next[id] || !isValidTodoMapKey(id)) return;
    patch[`${TODO_MAP_FIELD}.${id}`] = {
      id: prev[id]?.id ?? id,
      deleted: true,
      updatedAt: stamp
    };
  });
  return patch;
};

export const todosArrayToMapPatch = (todos, stamp = new Date().toISOString()) => {
  const patch = {};
  asTodoList(todos).forEach((todo) => {
    const id = todoMapKey(todo?.id);
    if (!id || !isValidTodoMapKey(id) || todo.deleted) return;
    patch[`${TODO_MAP_FIELD}.${id}`] = { ...todo, updatedAt: todo.updatedAt || stamp };
  });
  return patch;
};

export const CALORIE_MEMORY_FIELD = 'calorieMemory';

// The Calendar tab's board. Same hazard as the maps below, one level deeper: it is
// written as `sprintBoard.<section>` and `sprintBoard.<map>.<date>` paths, never at
// its bare name outside an explicit import. See lib/sprintBoard.js.
export const SPRINT_BOARD_FIELD = 'sprintBoard';

// Book ideas already in daily life. Written as `integrations.<id>`, never as a
// whole map, so one device adding an idea cannot erase the other's.
export const INTEGRATIONS_FIELD = 'integrations';

// Goals and their checklist rows. Written as `goals.<id>.<field>` and
// `goals.<id>.items.<itemId>`, never as the whole goal, so a title edit on one
// device cannot wipe a step check made on another.
export const GOALS_FIELD = 'goals';

// A map written at a bare field name is REPLACED wholesale by updateDoc, so two
// devices that both logged today would each flatten the other's entire history.
// Per-date field paths only touch the dates a save actually changed. calorieMemory
// and todoMap already work this way; these four are the same shape and same hazard.
export const DATE_KEYED_CLOUD_FIELDS = ['mealLog', 'moneyLog', 'stepLog', 'dayPlans'];

const CLOUD_DATE_KEY = /^\d{4}-\d{2}-\d{2}$/;

export const dateKeyedFieldFor = (key) =>
  DATE_KEYED_CLOUD_FIELDS.find((field) => String(key).startsWith(`${field}.`)) || null;

// BOTH maps must already be normalized by the same normalizer. Diffing a raw map
// against a normalized one never reaches a fixed point: the normalizers add fields
// (caloriesMin, updatedAt) that the raw entry lacks, so every date reads as changed
// on every pass and the write loop cannot settle. That is the v2.0.8 quota outage.
export const changedDateKeyPaths = (field, prevMap, nextMap) => {
  const prev = asMap(prevMap);
  const out = {};
  Object.entries(asMap(nextMap)).forEach(([date, value]) => {
    if (!CLOUD_DATE_KEY.test(date)) return;
    if (JSON.stringify(prev[date]) === JSON.stringify(value)) return;
    out[`${field}.${date}`] = value;
  });
  return out;
};

// A date the normalizer dropped has to be deleted explicitly, or the cloud keeps it
// forever and the next snapshot merges it straight back in.
export const removedDateKeyPaths = (field, prevMap, nextMap) => {
  const next = asMap(nextMap);
  return Object.keys(asMap(prevMap))
    .filter((date) => CLOUD_DATE_KEY.test(date)
      && !Object.prototype.hasOwnProperty.call(next, date))
    .map((date) => `${field}.${date}`);
};

// Two baselines, because there are two different questions and one answer cannot
// serve both.
//
// `changedAgainst` is what this caller last knew the CLOUD to hold. Narrowing against
// anything else is what made the snapshot push and the write disagree in v1: the push
// decided against the cloud while the write measured a local ref, so the cycle could
// never settle.
//
// `removedAgainst` is this DEVICE's own previous view, and it is optional. A date
// missing from the cloud baseline means only "this device never had it" — the local
// cache is trimmed on storage pressure and a fresh device starts empty — so deleting
// on that basis erases other devices' history. A date missing from the device's own
// prior view really was deleted by the user. Omit it and nothing is deleted, which is
// the right call for any writer that cannot tell the two apart (the snapshot handler).
export const narrowDateKeyedWrite = (updates, changedAgainst, removedAgainst = null) => {
  const next = { ...(updates || {}) };
  const removed = [];
  DATE_KEYED_CLOUD_FIELDS.forEach((field) => {
    if (next[field] === undefined) return;
    const nextMap = next[field];
    delete next[field];
    Object.assign(next, changedDateKeyPaths(field, asMap(changedAgainst)[field], nextMap));
    if (removedAgainst) {
      removed.push(...removedDateKeyPaths(field, asMap(removedAgainst)[field], nextMap));
    }
  });
  return { updates: next, removed };
};

const isDeleteSentinel = (value) =>
  !!value && typeof value === 'object' && typeof value._methodName === 'string'
  && /delete/i.test(value._methodName);

// Immutable set at a nested path. Without it a `sprintBoard.days.<date>` patch falls
// through to the catch-all below and is cached under a literal top-level key with
// dots in its name — the edit would then vanish on a reload before the next snapshot.
const setAtPath = (base, segments, value) => {
  const [head, ...rest] = segments;
  const node = { ...asMap(base) };
  if (rest.length) {
    node[head] = setAtPath(node[head], rest, value);
  } else if (isDeleteSentinel(value)) {
    delete node[head];
  } else {
    node[head] = value;
  }
  return node;
};

export const applyDottedPatch = (base, patch) => {
  const out = { ...(base || {}) };
  Object.entries(patch || {}).forEach(([key, value]) => {
    if (key === '_calorieMemoryRemove' || key === '_dateKeyRemove') return;
    if (key === 'updatedAt') {
      out.updatedAt = value;
      return;
    }
    if (key.startsWith(`${CALORIE_MEMORY_FIELD}.`)) {
      const memKey = key.slice(CALORIE_MEMORY_FIELD.length + 1);
      const nextMemory = { ...asMap(out.calorieMemory) };
      if (isDeleteSentinel(value)) delete nextMemory[memKey];
      else nextMemory[memKey] = value;
      out.calorieMemory = nextMemory;
      return;
    }
    if (key.startsWith(`${TODO_MAP_FIELD}.`)) {
      const id = key.slice(TODO_MAP_FIELD.length + 1);
      out.todoMap = { ...asMap(out.todoMap), [id]: value };
      return;
    }
    if (key === TODO_MAP_FIELD && value && typeof value === 'object' && !Array.isArray(value)) {
      out.todoMap = { ...asMap(out.todoMap), ...value };
      return;
    }
    const dateField = dateKeyedFieldFor(key);
    if (dateField) {
      const dateKey = key.slice(dateField.length + 1);
      const nextMap = { ...asMap(out[dateField]) };
      if (isDeleteSentinel(value)) delete nextMap[dateKey];
      else nextMap[dateKey] = value;
      out[dateField] = nextMap;
      return;
    }
    if (key.startsWith(`${INTEGRATIONS_FIELD}.`)) {
      const id = key.slice(INTEGRATIONS_FIELD.length + 1);
      const nextMap = { ...asMap(out[INTEGRATIONS_FIELD]) };
      if (isDeleteSentinel(value)) delete nextMap[id];
      else nextMap[id] = value;
      out[INTEGRATIONS_FIELD] = nextMap;
      return;
    }
    if (key.startsWith(`${GOALS_FIELD}.`)) {
      out[GOALS_FIELD] = setAtPath(
        out[GOALS_FIELD],
        key.slice(GOALS_FIELD.length + 1).split('.'),
        value
      );
      return;
    }
    if (key.startsWith(`${SPRINT_BOARD_FIELD}.`)) {
      out[SPRINT_BOARD_FIELD] = setAtPath(
        out[SPRINT_BOARD_FIELD],
        key.slice(SPRINT_BOARD_FIELD.length + 1).split('.'),
        value
      );
      return;
    }
    out[key] = value;
  });
  if (out.todoMap || out.todos) {
    const merged = mergeTodoState(out.todos, [], { cloudMap: out.todoMap });
    out.todos = merged.list;
    out.todoMap = merged.map;
  }
  return out;
};

// An edit parked by the budget, a spent quota, or an unhydrated doc lives in the
// pending queue until it can be sent. That queue was memory-only, so closing the tab
// dropped it — Firestore's own IndexedDB queue had been quietly covering the case,
// and removing that cache removes the cover. Persisting the queue restores the
// guarantee under code the budget and loop guard can still see.
export const PENDING_QUEUE_MAX = 200;

// A deleteField() sentinel cannot survive JSON: restoring one raw would write a
// literal {_methodName} object into the document instead of removing the field.
export const PENDING_DELETE_MARKER = '__rpg_delete_field__';

export const serializePendingUpdate = (updates) => {
  const out = {};
  Object.entries(updates || {}).forEach(([key, value]) => {
    if (value === undefined) return;
    if (isFirestoreSentinel(value)) {
      // deleteField() is the only sentinel this app queues. Any other one cannot be
      // rebuilt from JSON, so dropping it beats persisting something unwritable.
      if (value._methodName === 'deleteField') out[key] = PENDING_DELETE_MARKER;
      return;
    }
    out[key] = value;
  });
  return out;
};

export const deserializePendingUpdate = (updates, makeDeleteSentinel) => {
  const out = {};
  Object.entries(updates || {}).forEach(([key, value]) => {
    out[key] = value === PENDING_DELETE_MARKER ? makeDeleteSentinel() : value;
  });
  return out;
};

export const hasTodoMapPatch = (updates) =>
  patchKeys(updates).some((k) => k === TODO_MAP_FIELD || k.startsWith(`${TODO_MAP_FIELD}.`));

export const shouldUploadLocalCacheAsAuthority = ({
  fromCache,
  cacheNewer,
  cacheRicher,
  cloudUpdatedAt,
  cacheSavedAt
} = {}) => {
  if (fromCache) return false;
  if (!cacheNewer || !cacheRicher) return false;
  if (typeof cloudUpdatedAt === 'string' && cloudUpdatedAt) {
    const cloudMs = new Date(cloudUpdatedAt).getTime();
    const cacheMs = cacheSavedAt ? new Date(cacheSavedAt).getTime() : 0;
    if (Number.isFinite(cloudMs) && cloudMs >= cacheMs) return false;
  }
  return false;
};

export const stampForAppliedServerSnapshot = ({
  cloudUpdatedAt,
  mergedLocalOnlyTodos = false,
  cacheSavedAt
} = {}) => {
  if (mergedLocalOnlyTodos) {
    return new Date().toISOString();
  }
  if (typeof cloudUpdatedAt === 'string' && cloudUpdatedAt) return cloudUpdatedAt;
  return cacheSavedAt || new Date().toISOString();
};

export const cacheStampMatchesWrite = (cacheSavedAt, cloudUpdatedAt) => (
  typeof cacheSavedAt === 'string'
  && typeof cloudUpdatedAt === 'string'
  && cacheSavedAt === cloudUpdatedAt
);

// One shared save state drives seven status lines, each of which relabels it with its
// own tab's wording. Without a domain every line claims the write is its own, so six of
// the seven lie. The patch keys already say which data moved, so derive it from those.
export const CLOUD_WRITE_DOMAINS = {
  todos: 'quests',
  todoMap: 'quests',
  habits: 'training',
  dayPlans: 'plan',
  scheduleConfig: 'plan',
  scheduleData: 'plan',
  mealLog: 'meals',
  calorieMemory: 'meals',
  healthProfile: 'meals',
  stepLog: 'meals',
  moneyLog: 'money',
  monthlyJournal: 'journal',
  sprintBoard: 'calendar',
  integrations: 'integrated',
  goals: 'goals'
};

// A patch touching more than one domain (a full-document upload, say) has no single
// owner, so it reports as null and every line shows neutral wording.
export const cloudWriteDomain = (updates) => {
  const domains = new Set(
    Object.keys(updates || {})
      .filter((k) => k !== 'updatedAt' && k !== '_calorieMemoryRemove' && k !== '_dateKeyRemove')
      .map((k) => CLOUD_WRITE_DOMAINS[cloudUpdateRootKey(k)])
      .filter(Boolean)
  );
  return domains.size === 1 ? Array.from(domains)[0] : null;
};
